import { NavLink, useLocation } from 'react-router-dom'
import {
  Activity,
  LayoutDashboard,
  MessageSquare,
  BookUser,
  Smartphone,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  Zap,
} from 'lucide-react'
import { useSpring, animated } from '@react-spring/web'
import { useEffect, useState } from 'react'
import { cn } from '@/utils/cn'
import { canAccessPage, type PagePermission } from '@/utils/permissions'
import { useUIStore } from '@/stores/uiStore'
import { useAuth } from '@/hooks/useAuth'
import { Avatar } from '@/components/common/Avatar'
import { useBrandingStore } from '@/stores/brandingStore'

interface NavItem {
  label: string
  icon: React.ReactNode
  path?: string
  page?: PagePermission
  children?: Array<{
    path: string
    label: string
    icon: React.ReactNode
    page: PagePermission
  }>
}

const navItems: NavItem[] = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard size={20} />,
    page: 'dashboard',
  },
  {
    path: '/chat',
    label: 'Chat',
    icon: <MessageSquare size={20} />,
    page: 'chat',
  },
  {
    path: '/contacts',
    label: 'Contatos',
    icon: <BookUser size={20} />,
    page: 'contatos',
  },
  {
    path: '/monitoring',
    label: 'Monitoramento',
    icon: <Activity size={20} />,
    page: 'monitoramento',
  },
  {
    label: 'Configuracoes',
    icon: <Settings size={20} />,
    children: [
      {
        path: '/settings',
        label: 'Geral',
        icon: <Settings size={18} />,
        page: 'configuracoes.geral',
      },
      {
        path: '/instances',
        label: 'Instancias',
        icon: <Smartphone size={18} />,
        page: 'configuracoes.instancias',
      },
      {
        path: '/users',
        label: 'Usuarios',
        icon: <Users size={18} />,
        page: 'configuracoes.usuarios',
      },
    ],
  },
]

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUIStore()
  const { user, logout } = useAuth()
  const branding = useBrandingStore((state) => state)
  const location = useLocation()
  const [settingsOpen, setSettingsOpen] = useState(true)

  const sidebarSpring = useSpring({
    width: sidebarCollapsed ? 64 : 240,
    config: { tension: 280, friction: 28 },
  })

  useEffect(() => {
    if (
      location.pathname.startsWith('/settings') ||
      location.pathname.startsWith('/instances') ||
      location.pathname.startsWith('/users')
    ) {
      setSettingsOpen(true)
    }
  }, [location.pathname])

  const filteredItems = navItems
    .map((item) => ({
      ...item,
      children: item.children?.filter((child) => canAccessPage(user, child.page)),
    }))
    .filter((item) => {
      if (item.children) {
        return item.children.length > 0
      }
      return item.page ? canAccessPage(user, item.page) : true
    })

  return (
    <animated.aside
      style={sidebarSpring}
      className="glass-sidebar flex h-full shrink-0 flex-col overflow-hidden relative z-20"
    >
      <div
        className={cn(
          'flex h-[var(--header-height)] items-center border-b border-[var(--border-subtle)] px-4 shrink-0',
          sidebarCollapsed ? 'justify-center' : 'justify-between',
        )}
      >
        <div className="flex items-center gap-3 overflow-hidden">
          <div className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-gradient-to-br from-[var(--color-brand)] to-[var(--color-secondary)] shrink-0">
            <Zap size={16} className="text-white" />
          </div>
          {!sidebarCollapsed && (
            <span className="whitespace-nowrap text-lg font-bold text-[var(--text-primary)]">
              {branding.platform_name || 'ISing'}
            </span>
          )}
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-x-hidden overflow-y-auto px-3 py-4">
        {filteredItems.map((item) => {
          const hasChildren = Boolean(item.children?.length)
          const isActive = hasChildren
            ? item.children?.some((child) => location.pathname.startsWith(child.path))
            : item.path === '/monitoring'
              ? ['/monitoring', '/agents', '/agent-graphs', '/skills', '/tools', '/rag', '/executions'].some((path) =>
                  location.pathname.startsWith(path),
                )
              : Boolean(item.path && location.pathname.startsWith(item.path))

          if (hasChildren) {
            return (
              <div key={item.label} className="space-y-1">
                <button
                  type="button"
                  onClick={() => setSettingsOpen((current) => !current)}
                  className={cn(
                    'flex w-full items-center rounded-[var(--radius-md)] text-sm font-medium gap-3 transition-all duration-150',
                    sidebarCollapsed ? 'justify-center p-2.5' : 'px-3 py-2.5',
                    isActive
                      ? 'bg-[var(--color-brand-light)] text-[var(--color-brand)]'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--glass-medium)] hover:text-[var(--text-primary)]',
                  )}
                >
                  <span className="shrink-0">{item.icon}</span>
                  {!sidebarCollapsed && (
                    <>
                      <span className="flex-1 truncate text-left">{item.label}</span>
                      <ChevronDown
                        size={16}
                        className={cn(
                          'transition-transform duration-150',
                          settingsOpen ? 'rotate-0' : '-rotate-90',
                        )}
                      />
                    </>
                  )}
                </button>

                {!sidebarCollapsed && settingsOpen && (
                  <div className="ml-3 space-y-1 border-l border-[var(--border-subtle)] pl-3">
                    {item.children?.map((child) => {
                      const childActive = location.pathname.startsWith(child.path)
                      return (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          className={cn(
                            'flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2 text-sm transition-all duration-150',
                            childActive
                              ? 'bg-[var(--color-brand-light)] text-[var(--color-brand)]'
                              : 'text-[var(--text-secondary)] hover:bg-[var(--glass-medium)] hover:text-[var(--text-primary)]',
                          )}
                        >
                          <span className="shrink-0">{child.icon}</span>
                          <span className="truncate">{child.label}</span>
                        </NavLink>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          }

          return (
            <div key={item.path} className="group relative">
              <NavLink
                to={item.path!}
                className={cn(
                  'flex items-center rounded-[var(--radius-md)] text-sm font-medium gap-3 transition-all duration-150',
                  sidebarCollapsed ? 'justify-center p-2.5' : 'px-3 py-2.5',
                  isActive
                    ? 'bg-[var(--color-brand-light)] text-[var(--color-brand)]'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--glass-medium)] hover:text-[var(--text-primary)]',
                )}
              >
                <span className="shrink-0">{item.icon}</span>
                {!sidebarCollapsed && <span className="truncate whitespace-nowrap">{item.label}</span>}
              </NavLink>

              {sidebarCollapsed && (
                <div
                  className={cn(
                    'glass-tooltip pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 opacity-0 transition-opacity duration-150 group-hover:opacity-100',
                  )}
                >
                  {item.label}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      <button
        onClick={toggleSidebar}
        className={cn(
          'absolute top-1/2 -right-3 flex h-6 w-6 items-center justify-center rounded-full border border-[var(--border-medium)] bg-[var(--bg-primary)] text-[var(--text-muted)] shadow-[var(--shadow-sm)] z-30 transition-colors duration-150',
          'hover:bg-[var(--glass-medium)] hover:text-[var(--text-primary)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]',
        )}
        aria-label={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
      >
        {sidebarCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>

      <div
        className={cn(
          'border-t border-[var(--border-subtle)] px-3 py-4 shrink-0',
          sidebarCollapsed ? 'flex justify-center' : '',
        )}
      >
        {sidebarCollapsed ? (
          <div className="group relative">
            <button onClick={logout} className="flex items-center justify-center" aria-label="Logout">
              <Avatar name={user?.full_name ?? 'U'} src={user?.avatar_url} size="sm" />
            </button>
            <div className="glass-tooltip pointer-events-none absolute bottom-0 left-full z-50 ml-2 whitespace-nowrap opacity-0 transition-opacity duration-150 group-hover:opacity-100">
              {user?.full_name}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Avatar name={user?.full_name ?? 'U'} src={user?.avatar_url} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[var(--text-primary)]">{user?.full_name}</p>
              <p className="truncate text-xs capitalize text-[var(--text-muted)]">{user?.role}</p>
            </div>
            <button
              onClick={logout}
              className="rounded p-1 shrink-0 text-[var(--text-muted)] transition-colors duration-150 hover:text-[var(--color-error)]"
              aria-label="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>
    </animated.aside>
  )
}
